import React, { useState, useEffect, useCallback } from 'react';
import NavBarTwo from '../components/NavBarTwo';
import axios from 'axios';
import FooterTwo from '../components/FooterTwo';

const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  const baseClasses = 'p-4 rounded-md mb-6 flex justify-between items-center';
  const typeClasses = {
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
  };
  return (
    <div className={`${baseClasses} ${typeClasses[type]}`}>
      <span>{message}</span>
      <button onClick={onClose} className="font-bold text-lg">
        &times;
      </button>
    </div>
  );
};

const Backoffice = () => {
  const [activeTab, setActiveTab] = useState('add');
  const [realisation, setRealisation] = useState({
    nom: '',
    description: '',
    image: [], // --- MODIFICATION: Utilise un tableau pour stocker les images
  });
  const [realisationId, setRealisationId] = useState(null);
  const [realisationsList, setRealisationsList] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]); // --- MODIFICATION: Utilise un tableau
  const [newImagePreviewUrls, setNewImagePreviewUrls] = useState([]); // --- MODIFICATION: Utilise un tableau
  const [notification, setNotification] = useState({ message: '', type: '' });

  const fetchRealisationsList = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_STRAPI_BASE_URL}/api/realisations`
      );
      setRealisationsList(response.data.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des réalisations :', error);
      setNotification({
        message: 'Erreur: Impossible de charger la liste des réalisations.',
        type: 'error',
      });
    }
  }, []);

  useEffect(() => {
    fetchRealisationsList();
  }, [fetchRealisationsList]);

  const resetForm = () => {
    setRealisation({ nom: '', description: '', image: [] }); // --- MODIFICATION: Réinitialise avec un tableau vide
    setRealisationId(null);
    setExistingImageUrls([]); // --- MODIFICATION: Réinitialise le tableau
    setNewImagePreviewUrls([]); // --- MODIFICATION: Réinitialise le tableau
    const fileInput = document.getElementById('image');
    if (fileInput) fileInput.value = '';
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    resetForm();
    setNotification({ message: '', type: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRealisation({ ...realisation, [name]: value });
  };

  const handleImageChange = (e) => {
    // --- MODIFICATION: Gère plusieurs fichiers
    const files = Array.from(e.target.files);
    setRealisation({ ...realisation, image: files });
    const previewUrls = files.map((file) => URL.createObjectURL(file));
    setNewImagePreviewUrls(previewUrls);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification({ message: '', type: '' });
    if (activeTab === 'edit' && !realisationId) {
      alert('Veuillez sélectionner une réalisation à modifier.');
      return;
    }
    if (!realisation.nom.trim() || !realisation.description.trim()) {
      alert('Les champs "Nom" et "Description" sont requis.');
      return;
    }
    if (activeTab === 'add' && realisation.image.length === 0) {
      alert("Le champ 'Image' est requis pour l'ajout.");
      return;
    }
    const formData = new FormData();
    formData.append(
      'data',
      JSON.stringify({
        nom: realisation.nom,
        description: realisation.description,
      })
    );

    // --- MODIFICATION: Itère sur le tableau d'images pour les ajouter au FormData
    realisation.image.forEach((file) => {
      formData.append('files.image', file, file.name);
    });

    try {
      const url =
        activeTab === 'edit'
          ? `${process.env.REACT_APP_STRAPI_BASE_URL}/api/realisations/${realisationId}`
          : `${process.env.REACT_APP_STRAPI_BASE_URL}/api/realisations`;
      const method = activeTab === 'edit' ? 'put' : 'post';
      await axios({ method, url, data: formData });
      setNotification({
        message: `Réalisation ${
          activeTab === 'edit' ? 'modifiée' : 'ajoutée'
        } avec succès !`,
        type: 'success',
      });
      resetForm();
      fetchRealisationsList();
      setActiveTab('add');
    } catch (error) {
      console.error("Erreur lors de l'opération :", error);
      setNotification({
        message: "Une erreur est survenue lors de l'opération.",
        type: 'error',
      });
    }
  };

  const handleSelectRealisation = async (e) => {
    const selectedId = e.target.value;
    resetForm();
    setNotification({ message: '', type: '' });

    if (!selectedId) return;

    setRealisationId(selectedId);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_STRAPI_BASE_URL}/api/realisations/${selectedId}?populate=deep`
      );
      const data = response.data.data.attributes;

      setRealisation({
        nom: data.nom || '',
        description: data.description || '',
        image: [],
      });

      // --- MODIFICATION: Gère les URLs d'images existantes
      const imageUrls = getImageUrlPathsFromStrapi(data);
      if (imageUrls.length > 0) {
        const fullUrls = imageUrls.map(
          (path) => `${process.env.REACT_APP_STRAPI_BASE_URL}${path}`
        );
        setExistingImageUrls(fullUrls);
      } else {
        setNotification({
          message: "Info : Aucune image n'est associée à cette réalisation.",
          type: 'info',
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sélection de la réalisation:', error);
      setNotification({
        message: 'Erreur lors du chargement de la réalisation.',
        type: 'error',
      });
    }
  };

  // --- NOUVELLE FONCTION POUR TROUVER PLUSIEURS URLS ---
  const getImageUrlPathsFromStrapi = (data) => {
    const imageUrls = [];
    const fieldsToTry = ['image', 'Image', 'photo', 'Photo', 'thumbnail'];
    for (const field of fieldsToTry) {
      const mediaField = data[field];
      if (Array.isArray(mediaField?.data)) {
        mediaField.data.forEach((item) => {
          if (item?.attributes?.url) {
            imageUrls.push(item.attributes.url);
          }
        });
      } else if (mediaField?.data?.attributes?.url) {
        imageUrls.push(mediaField.data.attributes.url);
      }
    }
    return imageUrls;
  };

  const handleDeleteRealisation = async () => {
    if (!realisationId) {
      alert('Veuillez sélectionner une réalisation à supprimer.');
      return;
    }
    if (
      window.confirm('Êtes-vous sûr de vouloir supprimer cette réalisation ?')
    ) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_STRAPI_BASE_URL}/api/realisations/${realisationId}`
        );
        setNotification({
          message: 'Réalisation supprimée avec succès !',
          type: 'success',
        });
        resetForm();
        fetchRealisationsList();
      } catch (error) {
        console.error('Erreur lors de la suppression :', error);
        setNotification({
          message: 'Une erreur est survenue lors de la suppression.',
          type: 'error',
        });
      }
    }
  };

  const inputClasses =
    'w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-marronfonce focus:border-transparent transition duration-200';

  return (
    <div className="bg-marronclair min-h-screen flex flex-col">
      <NavBarTwo />
      <main className="flex-grow container mx-auto p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-8 border-b-2 border-gray-300">
            {['add', 'edit', 'delete'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-6 py-3 text-lg font-semibold border-b-4 transition-colors duration-300 ${
                  activeTab === tab
                    ? 'border-marronfonce text-marronfonce'
                    : 'border-transparent text-gray-500 hover:text-marronfonce'
                }`}
              >
                {tab === 'add'
                  ? 'Ajouter'
                  : tab === 'edit'
                  ? 'Modifier'
                  : 'Supprimer'}
              </button>
            ))}
          </div>
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification({ message: '', type: '' })}
          />
          <div className="bg-white p-8 rounded-xl shadow-lg">
            {activeTab === 'add' || activeTab === 'edit' ? (
              <form onSubmit={handleSubmit} className="space-y-8">
                <h2 className="text-3xl font-bold text-marronfonce text-center mb-6">
                  {activeTab === 'add'
                    ? 'Ajouter une nouvelle réalisation'
                    : 'Modifier une réalisation'}
                </h2>
                {activeTab === 'edit' && (
                  <div>
                    <label className="block text-lg font-medium text-marronfonce mb-2">
                      Sélectionnez une réalisation
                    </label>
                    <select
                      value={realisationId || ''}
                      onChange={handleSelectRealisation}
                      className={inputClasses}
                      required
                    >
                      <option value="">-- Choisissez une option --</option>
                      {realisationsList.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.attributes.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {(activeTab === 'add' || realisationId) && (
                  <>
                    <div>
                      <label
                        htmlFor="nom"
                        className="block text-lg font-medium text-marronfonce mb-2"
                      >
                        Nom
                      </label>
                      <input
                        id="nom"
                        name="nom"
                        type="text"
                        value={realisation.nom}
                        onChange={handleChange}
                        className={inputClasses}
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="description"
                        className="block text-lg font-medium text-marronfonce mb-2"
                      >
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={realisation.description}
                        onChange={handleChange}
                        className={inputClasses}
                        rows="4"
                        required
                      />
                    </div>
                    <div>
                      {existingImageUrls.length > 0 && (
                        <div className="mb-4">
                          <p className="block text-lg font-medium text-marronfonce mb-2">
                            Images actuelles :
                          </p>
                          <div className="flex space-x-4 overflow-x-auto">
                            {existingImageUrls.map((url, index) => (
                              <img
                                key={index}
                                src={url}
                                alt={`Image actuelle de la réalisation ${
                                  index + 1
                                }`}
                                className="w-48 h-auto rounded-md object-cover border"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {newImagePreviewUrls.length > 0 && (
                        <div className="mb-4">
                          <p className="block text-lg font-medium text-marronfonce mb-2">
                            Aperçu des nouvelles images :
                          </p>
                          <div className="flex space-x-4 overflow-x-auto">
                            {newImagePreviewUrls.map((url, index) => (
                              <img
                                key={index}
                                src={url}
                                alt={`Aperçu de la nouvelle image ${index + 1}`}
                                className="w-48 h-auto rounded-md object-cover border"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      <label
                        htmlFor="image"
                        className="block text-lg font-medium text-marronfonce mb-2"
                      >
                        {activeTab === 'edit'
                          ? "Remplacer l'image (optionnel)"
                          : 'Ajouter une ou plusieurs images'}
                      </label>
                      <input
                        id="image"
                        name="image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        multiple // --- MODIFICATION: Permet la sélection de plusieurs fichiers
                        className={`${inputClasses} p-0 file:p-3 file:border-0 file:bg-gray-100 file:mr-4 file:font-semibold file:text-marronfonce hover:file:bg-gray-200`}
                        required={activeTab === 'add'}
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full p-4 bg-marronfonce text-white text-lg font-bold rounded-lg hover:bg-opacity-90 transition-colors duration-300"
                    >
                      {activeTab === 'add'
                        ? 'Ajouter la Réalisation'
                        : 'Mettre à jour'}
                    </button>
                  </>
                )}
              </form>
            ) : (
              <div className="text-center space-y-6">
                <h2 className="text-3xl font-bold text-marronfonce mb-6">
                  Supprimer une réalisation
                </h2>
                <label className="block text-lg font-medium text-marronfonce mb-2">
                  Sélectionnez la réalisation à supprimer
                </label>
                <select
                  value={realisationId || ''}
                  onChange={(e) => setRealisationId(e.target.value)}
                  className={inputClasses}
                  required
                >
                  <option value="" disabled>
                    -- Choisissez une option --
                  </option>
                  {realisationsList.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.attributes.nom}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleDeleteRealisation}
                  className="w-full p-4 bg-red-600 text-white text-lg font-bold rounded-lg hover:bg-red-700 transition-colors duration-300 disabled:bg-gray-400"
                  disabled={!realisationId}
                >
                  Supprimer Définitivement
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <FooterTwo />
    </div>
  );
};

export default Backoffice;
